import React from 'react';
import { Box, Button, Grid, IconButton, InputAdornment, TextField, Typography, styled } from '@mui/material';
import { Add, AutoAwesome, Close, FolderOpen, NoteAdd, Search } from '@mui/icons-material';
import { useAppSelector } from 'store/hooks';
import { ApplicationState } from 'store/store';
import { EmptyState } from 'components/utils/emptyState';
import { PerkTemplate } from 'components/perks/templates/models/PerkTemplate';
import { PerkTemplateCard } from 'components/perks/templates/PerkTemplateCard';
import { PerkSelectionModal } from 'components/perks/templates/PerkSelectionModal';
import { PerksCreationModal } from 'components/perks/modals/PerksCreationModal';
import { ChoiceModal, ChoiceModalOption } from 'components/perks/modals/ChoiceModal';
import { DeleteDialog } from 'ui/dialogs/DeleteDialog';
import { SplitButton } from 'ui/SplitButton';
import { SplitButtonStorageKey } from 'ui/splitButtonStorage';
import { usePerkTemplatesData } from 'components/perks/hooks/perk-templates/usePerkTemplatesData';
import { usePerkTemplatesModals } from 'components/perks/hooks/perk-templates/usePerkTemplatesModals';
import { usePerkTemplatesMutation } from 'components/perks/hooks/perk-templates/usePerkTemplatesMutation';
import EmptyImage from 'static/perks-empty-state.svg';

const StyledBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    margin: theme.spacing(1, 0, 1, 0),
    alignItems: 'flex-end',
    justifyContent: 'space-between'
}));

export const PerkTemplatesView: React.FC = () => {
    const {
        templates,
        loading,
        searchValue,
        filteredUserTemplates,
        filteredSuggestedTemplates,
        handleSearchChange,
        handleClearSearch
    } = usePerkTemplatesData();
    const {
        hasPerks,
        createModalOpen,
        perkSelectionOpen,
        choiceModalOpen,
        templateMode,
        duplicatePerk,
        templateToDelete,
        closeConfirmOpen,
        splitButtonOptions,
        handleStartFromScratch,
        handleFromExistingPerk,
        handleEmptyStateCreateClick,
        handleCloseChoiceModal,
        handlePerkSelected,
        handleEditTemplate,
        handleViewTemplate,
        handleRequestClose,
        handleConfirmClose,
        handleDismissClose,
        handleTemplateSubmit,
        handleDeleteClick,
        handleDeleteClose,
        handleClosePerkSelection
    } = usePerkTemplatesModals();

    const choiceModalOptions = React.useMemo<ChoiceModalOption[]>(
        () => [
            {
                id: 'from-scratch',
                icon: <NoteAdd fontSize="inherit" />,
                label: 'Start from scratch',
                description: 'Create a brand new template with custom settings',
                onClick: handleStartFromScratch
            },
            {
                id: 'from-perk',
                icon: <FolderOpen fontSize="inherit" />,
                label: 'Create from existing perk',
                description: 'Pre-filled settings from an existing perk',
                onClick: handleFromExistingPerk
            }
        ],
        [handleStartFromScratch, handleFromExistingPerk]
    );
    const { deleteLoading, handleDeleteConfirm } = usePerkTemplatesMutation(
        templateToDelete,
        handleDeleteClose
    );

    const perks = useAppSelector((state: ApplicationState) => state.perks.perks);
    const currencySymbol = useAppSelector(
        (state: ApplicationState) => state.settings.settings.region?.currencySymbol
    );
    const primaryActionBackgroundColour = useAppSelector(
        (state: ApplicationState) =>
            state.content.currentLayout?.anchor.properties.primaryActionBackgroundColour
    );

    const renderUserTemplate = React.useCallback(
        (template: PerkTemplate) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={template._id}>
                <PerkTemplateCard
                    template={template}
                    onEdit={handleEditTemplate}
                    onDelete={handleDeleteClick}
                />
            </Grid>
        ),
        [handleDeleteClick, handleEditTemplate]
    );

    const renderSuggestedTemplate = React.useCallback(
        (template: PerkTemplate) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={template._id}>
                <PerkTemplateCard template={template} onEdit={handleViewTemplate} suggested />
            </Grid>
        ),
        [handleViewTemplate]
    );

    const renderCancelModal = React.useCallback(
        () => (
            <DeleteDialog
                open={closeConfirmOpen}
                onClose={handleDismissClose}
                onDelete={handleConfirmClose}
                submitLabel="Yes, Cancel"
                discardLabel="No, don't cancel"
                title="Hold On!"
                content="Are you sure you want to cancel? All changes will be lost."
            />
        ),
        [closeConfirmOpen, handleConfirmClose, handleDismissClose]
    );

    if (!loading && templates.length === 0) {
        return (
            <>
                <EmptyState
                    headerText="There are no templates."
                    paragraphText="Create a template to get started."
                    buttonText="Create a Template"
                    onClick={handleEmptyStateCreateClick}
                    imageUrl={EmptyImage}
                />
                <PerksCreationModal
                    open={createModalOpen}
                    onClose={handleRequestClose}
                    onSubmit={handleTemplateSubmit}
                    currencySymbol={currencySymbol}
                    primaryActionBackgroundColour={primaryActionBackgroundColour}
                    templateMode={templateMode}
                    duplicatePerk={duplicatePerk}
                />
                <PerkSelectionModal
                    open={perkSelectionOpen}
                    onClose={handleClosePerkSelection}
                    onSelect={handlePerkSelected}
                    perks={perks}
                />
                <ChoiceModal
                    open={choiceModalOpen}
                    onClose={handleCloseChoiceModal}
                    title="Create a Template"
                    subtitle="How would you like to get started?"
                    options={choiceModalOptions}
                />
                {renderCancelModal()}
            </>
        );
    }

    return (
        <Box pb={2}>
            <StyledBox>
                <TextField
                    variant="outlined"
                    placeholder="Search templates..."
                    value={searchValue}
                    onChange={handleSearchChange}
                    size="small"
                    sx={{ minWidth: 300 }}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search color="action" />
                                </InputAdornment>
                            ),
                            endAdornment: searchValue ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={handleClearSearch}>
                                        <Close fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : undefined
                        }
                    }}
                />
                {hasPerks ? (
                    <SplitButton
                        size="small"
                        options={splitButtonOptions}
                        storageKey={SplitButtonStorageKey.TEMPLATE_CREATE}
                    />
                ) : (
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleStartFromScratch}
                        size="small"
                    >
                        Create
                    </Button>
                )}
            </StyledBox>
            {filteredUserTemplates.length > 0 && (
                <>
                    <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                        Your Templates
                    </Typography>
                    <Grid container spacing={3}>
                        {filteredUserTemplates.map(renderUserTemplate)}
                    </Grid>
                </>
            )}
            {filteredSuggestedTemplates.length > 0 && (
                <>
                    <Box display="flex" alignItems="center" gap={1} sx={{ mt: 3, mb: 0.5 }}>
                        <AutoAwesome color="action" />
                        <Typography variant="h6">Suggested Templates</Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                        Curated templates to help you get started quickly
                    </Typography>
                    <Grid container spacing={3}>
                        {filteredSuggestedTemplates.map(renderSuggestedTemplate)}
                    </Grid>
                </>
            )}
            <PerksCreationModal
                open={createModalOpen}
                onClose={handleRequestClose}
                onSubmit={handleTemplateSubmit}
                currencySymbol={currencySymbol}
                primaryActionBackgroundColour={primaryActionBackgroundColour}
                templateMode={templateMode}
                duplicatePerk={duplicatePerk}
            />
            <PerkSelectionModal
                open={perkSelectionOpen}
                onClose={handleClosePerkSelection}
                onSelect={handlePerkSelected}
                perks={perks}
            />
            <DeleteDialog
                open={!!templateToDelete}
                onClose={handleDeleteClose}
                onDelete={handleDeleteConfirm}
                loading={deleteLoading}
                title="Delete Template"
                content={`Are you sure you want to delete the template "${templateToDelete?.name}"?`}
            />
            <ChoiceModal
                open={choiceModalOpen}
                onClose={handleCloseChoiceModal}
                title="Create a Template"
                subtitle="How would you like to get started?"
                options={choiceModalOptions}
            />
            {renderCancelModal()}
        </Box>
    );
};
